import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { scrollToId } from "./SmoothScroll";
import { useLang } from "@/i18n";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { id: "ueber", label: t.nav.ueber },
    { id: "leistungen", label: t.nav.leistungen },
    { id: "referenzen", label: t.nav.referenzen },
    { id: "standort", label: t.nav.standort },
    { id: "kontakt", label: t.nav.kontakt },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  const LangToggle = () => (
    <div
      className="flex items-center border border-white/10 rounded-full overflow-hidden"
      data-testid="lang-toggle"
    >
      {["de", "it"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}`}
          onClick={() => setLang(l)}
          className={`px-3 py-1 font-mono2 text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${
            lang === l ? "bg-[var(--accent)] text-black" : "text-[var(--txt-dim)] hover:text-[var(--txt)]"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,border-color,padding] duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10 py-3" : "border-b border-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center justify-end">
        <div className="flex items-center gap-6 md:gap-9">
          <nav className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-[var(--txt-dim)] hover:text-[var(--txt)] transition-colors duration-300 link-underline"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <LangToggle />

          <button
            className="md:hidden font-mono2 text-[11px] tracking-[0.2em] uppercase text-[var(--txt)]"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-toggle"
          >
            {open ? "×" : "☰"}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-black/80 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  className="text-left font-mono2 text-sm tracking-[0.2em] uppercase text-[var(--txt-dim)]"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
